import { useCallback, useState } from 'react'
import cn from 'classnames'

import ImagePrepare, { type ImageParamsType } from 'app/common/components/ImagePrepare'
import { ImageClickable } from 'app/common/ui-next/plain-image-selector'
import PlainButton from 'app/common/ui-next/plain-button'
import { api } from 'app/api'
import postImage from 'app/common/actions/files/postImage'
import { dispatchFormChanged } from 'app/common/ui-next/custom-events'
import usePopupErr from 'app/common/hooks/usePopupErr'

import styles from './PhotoSelector.module.scss'

export const PhotoSelector = ({
  id,
  className,
  photoUrl,
  description,
  locked,
  linkText = '+ Добавить фото',
  deleteMessage,
}: {
  id: string
  className?: string
  photoUrl?: string
  description: string
  locked?: boolean
  linkText?: string
  deleteMessage?: string
}) => {
  const [state, setState] = useState<ImageParamsType | null>()

  const [popupErr] = usePopupErr()

  const handleSaveImage = useCallback(
    async (imagePrams: ImageParamsType, _: string, file: File) => {
      const data = new FormData()
      data.set('file', file)
      if (imagePrams.w !== undefined) data.set('w', String(imagePrams.w))
      if (imagePrams.x1 !== undefined) data.set('x1', String(imagePrams.x1))
      if (imagePrams.x2 !== undefined) data.set('x2', String(imagePrams.x2))
      if (imagePrams.y1 !== undefined) data.set('y1', String(imagePrams.y1))
      if (imagePrams.y2 !== undefined) data.set('y2', String(imagePrams.y2))

      const postedFile = await postImage({
        data,
      })

      if (postedFile) {
        setState({
          ...imagePrams,
          file: postedFile,
        })
        dispatchFormChanged()
        return true
      }
      popupErr('Не удалось загрузить изображение')
      return false
    },
    [popupErr],
  )

  const handleDeleteImage = useCallback(async (id: string) => {
    setState(null)
    dispatchFormChanged()
    return true
  }, [])

  if (locked) {
    if (photoUrl) {
      return (
        <ImageClickable
          id={id}
          className={cn(styles.image, className)}
          imageUrl={photoUrl}
          description={description}
          locked
        />
      )
    }
    return null
  }

  return (
    <>
      <ImagePrepare
        onSubmit={handleSaveImage}
        rectMinSizeWidth={140}
        rectMinSizeHeight={140}
        rectProportion={1}
        accept="image/png, image/jpg, image/jpeg"
        doNotUploadForPreview
      >
        {(openFile: () => void) => {
          const imageUrl = state === undefined ? photoUrl : state === null ? '' : state?.file

          if (imageUrl) {
            const fullPath = /^https:\/\//.test(imageUrl || '')
              ? imageUrl
              : imageUrl
                ? `${api.baseUrl}images/tmp/${imageUrl}`
                : ''

            return (
              <ImageClickable
                id={id}
                className={cn(styles.image, className)}
                imageUrl={fullPath}
                description={description}
                deleteMessage={deleteMessage}
                onReplace={openFile}
                onDelete={handleDeleteImage}
              />
            )
          }
          return (
            <div className={cn(styles.placeholder, className)}>
              <PlainButton onClick={openFile} disabled={locked}>
                {linkText}
              </PlainButton>
            </div>
          )
        }}
      </ImagePrepare>
      <input
        name={id}
        value={state === undefined ? photoUrl : state === null ? 'null' : state?.file}
        hidden
      />
    </>
  )
}
